import { CommonModule } from '@angular/common'
import { Component, HostBinding, Input } from '@angular/core'
import { NavFavoritesService } from '@employer/app/services/nav-favorites.service'
import { LetDirective } from '@ngrx/component'
import { TranslateModule } from '@ngx-translate/core'

export type ScreenTone = 'openings' | 'engage' | 'neutral'

/**
 * The title area at the top of a screen: a coloured eyebrow, the screen's name, a sentence saying
 * what the screen is *for*, and the action row.
 *
 * The eyebrow colour is the whole point — Engage takes the primary accent and Openings the
 * secondary, so the two lists read apart at a glance rather than only by their title. The subtitle
 * is a verb sentence for the same reason: "Move candidates through your stages" states intent where
 * a bare noun heading states category.
 *
 * Actions are projected, so each screen keeps its own buttons; the favourite star is built in
 * because every screen that has a title area is also a favouritable destination.
 */
@Component({
  selector: 'screen-header',
  standalone: true,
  imports: [CommonModule, LetDirective, TranslateModule],
  template: `
    <header class="screen-header">
      <div class="sh-main">
        <p class="sh-kicker" *ngIf="kicker">
          <span class="material-symbols-outlined md-18" *ngIf="icon">{{ icon }}</span>
          {{ kicker | translate }}
        </p>
        <h2 class="sh-title">{{ title | translate }}</h2>
        <p class="sh-subtitle" *ngIf="subtitle">{{ subtitle | translate }}</p>
      </div>

      <div class="sh-actions">
        <ng-container *ngrxLet="favorites$ as favorites">
          <button
            *ngIf="favoriteId"
            type="button"
            class="sh-fav"
            [class.is-on]="isFavorite(favorites)"
            [attr.aria-pressed]="isFavorite(favorites)"
            [title]="(isFavorite(favorites) ? 'Remove from Favorites' : 'Add to Favorites') | translate"
            [attr.aria-label]="
              ((isFavorite(favorites) ? 'Remove from Favorites' : 'Add to Favorites') | translate) + ': ' + (favoriteName | translate)
            "
            (click)="toggleFavorite()"
          >
            <span class="material-symbols-outlined md-18" [class.star-filled]="isFavorite(favorites)">star</span>
            <span class="sh-fav-label">{{ (isFavorite(favorites) ? 'Favorited' : 'Favorite') | translate }}</span>
          </button>
        </ng-container>
        <ng-content></ng-content>
      </div>
    </header>
  `,
  styles: [
    `
      /* The title area and the tab row below it sit on one white band that runs the full width of
         the content region, so the rule under the tabs reaches both edges instead of stopping at
         the gutter. Breaking out means undoing the shell's content padding (16px below md, 24px
         from there up — see main-layout) and re-adding it inside. */
      :host {
        display: block;
        margin: -1rem -1rem 0 -1rem;
        /* The bottom padding is the band's own spacing. It cannot live on .screen-header as a
           margin: with no bottom padding or border on the host, that margin collapses straight
           through the host's bottom edge and the gap lands outside the white. */
        padding: 1rem 1rem 20px;
        background: rgb(var(--color-white));
        /* The mockup's .band carries a bottom rule, so the title area is divided from the tab row
           rather than running into it. */
        border-bottom: 1px solid rgb(var(--color-app-borders));
      }

      @media (min-width: 768px) {
        :host {
          margin: -1.5rem -1.5rem 0;
          padding: 1.5rem 1.5rem 20px;
        }
      }

      /* The wash the mockup carries: the section's accent fading out down the band, so the context
         colour is present as a field and not only in the eyebrow. Layered over white so the band
         stays opaque. */
      :host(.tone-engage) {
        border-left: 4px solid rgb(var(--color-primary-accent));
        background: linear-gradient(180deg, rgba(var(--color-primary-accent), 0.06), rgba(var(--color-white), 0) 140%), rgb(var(--color-white));
      }

      :host(.tone-openings) {
        border-left: 4px solid rgb(var(--color-secondary));
        background: linear-gradient(180deg, rgba(var(--color-secondary), 0.06), rgba(var(--color-white), 0) 140%), rgb(var(--color-white));
      }

      .screen-header {
        display: flex;
        align-items: flex-end;
        flex-wrap: wrap;
        gap: 13px 18px;
      }

      .sh-main {
        flex: 1 1 260px;
        min-width: 0;
      }

      /* The e11 theme sets a flat line-height on the universal selector — 1.3rem, i.e. 20.8px, on every
         element regardless of its font-size. That is why the hero stack reads flat: an 11px eyebrow
         and a 21px heading get the same line box, so the heading loses the air the mockup gives it
         and the eyebrow gains air it should not have. Each role below restates the mockup's
         unitless 1.55 so the leading scales with the type again. */
      .sh-kicker {
        display: flex;
        align-items: center;
        gap: 9px;
        margin: 0;
        font-size: 12px;
        line-height: 1.55;
        font-weight: 600;
        letter-spacing: 0.13em;
        text-transform: uppercase;
        color: rgb(var(--color-grey));
      }
      :host(.tone-engage) .sh-kicker {
        color: rgb(var(--color-primary-accent));
      }
      :host(.tone-openings) .sh-kicker {
        color: rgb(var(--color-secondary));
      }

      /* The h2 inherits a heavier global weight; the mockup's heading is 21px/600. */
      .sh-title {
        margin: 7px 0 2px;
        font-size: 23px;
        font-weight: 600;
        letter-spacing: -0.018em;
        line-height: 1.55;
      }

      /* Matches the mockup's .band .verb rule. The colour is the mockup's --text-2, a cooler and
         lighter grey than the theme's --color-text-base (#4F4F4F) — and the same value the table
         body already uses, so the sentence under the title and the rows below it agree. */
      .sh-subtitle {
        margin: 0;
        font-size: 15px;
        line-height: 1.55;
        font-weight: 400;
        color: #5b6672;
      }

      .sh-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }

      /* The height every action in this row matches. The e11-button beside it is forced to the
         same figure from the consuming screen's stylesheet — see _openings-list-chrome.scss. */
      .sh-fav {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 39.8px;
        padding: 0 14.3px;
        border: 1px solid rgb(var(--color-app-borders));
        border-radius: 7px;
        background: transparent;
        color: rgb(var(--color-text-base));
        font-size: 14.3px;
        font-weight: 500;
        cursor: pointer;
        transition: border-color 0.13s ease, color 0.13s ease, background 0.13s ease;
      }
      .sh-fav:hover {
        border-color: rgb(var(--color-primary-accent));
        color: rgb(var(--color-primary-accent));
      }
      .sh-fav:focus-visible {
        outline: 2px solid rgb(var(--color-primary-accent));
        outline-offset: 2px;
      }
      .sh-fav.is-on {
        border-color: rgba(var(--color-primary-accent), 0.4);
        background: rgba(var(--color-primary-accent), 0.08);
        color: rgb(var(--color-primary-accent));
      }

      /* Material Symbols is served with FILL as a 0..1 range, so the filled star is the same
         glyph with the axis set — not a second ligature. */
      .star-filled {
        font-variation-settings: 'FILL' 1;
      }

      @media (max-width: 640px) {
        .sh-fav-label {
          display: none;
        }
        .sh-fav {
          padding: 0 10px;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .sh-fav {
          transition: none;
        }
      }
    `,
  ],
})
export class ScreenHeaderComponent {
  @Input() kicker = ''
  @Input() icon = ''
  @Input({ required: true }) title = ''
  @Input() subtitle = ''
  @Input() tone: ScreenTone = 'neutral'

  @HostBinding('class.tone-openings') get isOpenings() {
    return this.tone === 'openings'
  }
  @HostBinding('class.tone-engage') get isEngage() {
    return this.tone === 'engage'
  }

  /**
   * Writes the shared `employer_nav_favorites` list — the same one the left-nav and the settings
   * hub stars write. Starring a screen promotes it into the rail's Favorites section, which is what
   * makes moving a leaf onto a tab a fair trade rather than a demotion.
   */
  @Input() favoriteId = ''
  /** Qualified name for the Favorites list, where the entry sits out of its section's context. */
  @Input() favoriteName = ''

  readonly favorites$ = this.navFavorites.favorites$

  constructor(private navFavorites: NavFavoritesService) {}

  isFavorite(favorites: string[] | null): boolean {
    return !!this.favoriteId && !!favorites?.includes(this.favoriteId)
  }

  toggleFavorite(): void {
    if (this.favoriteId) this.navFavorites.toggleFavorite(this.favoriteId)
  }
}
